import { useQuery } from "@apollo/client";
import { Stack } from "@mui/material";
import { FacilityReadOnlyBanner } from "components/FacilityReadOnlyBanner";
import { gql } from "generated-graphql";
import { ProgramArea } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { getWasteBreadcrumb, getWasteContactsBreadcrumb } from "util/breadcrumb";
import { isRcraContactIssue } from "util/facilityContacts";

import { FacilityContacts } from "../../Contacts/FacilityContacts";

const GET_WASTE_FACILITY_CONTACTS = gql(`
  query GetWasteFacilityContacts($facilityId: ID!, $programArea: ProgramArea) {
    facility(id: $facilityId) {
      id
      name
      state
      ...facilityContacts
      issues {
        ...issue
      }
    }
  }
`);

export const WasteContacts = () => {
  const { tenantId, facilityId } = useParams();
  const { isReadOnly } = useFacilityReadOnly(facilityId);
  const { data, previousData } = useQuery(GET_WASTE_FACILITY_CONTACTS, {
    variables: {
      facilityId: facilityId ?? "",
      programArea: ProgramArea.Rcra,
    },
    skip: !facilityId,
    fetchPolicy: "cache-and-network",
  });
  const issues = useMemo(() => data?.facility?.issues ?? [], [data?.facility?.issues]);

  // Project each contact's reportingRoles to only the RCRA roles. The API
  // returns contacts that have at least one RCRA role (loader-level filter),
  // but a contact with mixed roles still carries their EPCRA roles in the
  // array — strip them so the Waste contacts grid stays RCRA-only.
  const facilityContacts = useMemo(() => {
    return (data?.facility?.contacts ?? [])
      .map((contact) => ({
        ...contact,
        reportingRoles: contact.reportingRoles.filter((role) => role.startsWith("RCRA")),
      }))
      .filter((contact) => contact.reportingRoles.length > 0);
  }, [data?.facility?.contacts]);

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? getWasteBreadcrumb(tenantId) : undefined,
    },
    {
      label: "Contacts",
      to: tenantId ? getWasteContactsBreadcrumb(tenantId) : undefined,
    },
    {
      label: data?.facility?.name ?? "",
    },
  ]);

  return (
    <Stack sx={{ px: 3, pt: 4 }}>
      <FacilityReadOnlyBanner facilityId={facilityId} />
      <FacilityContacts
        loading={!data && !previousData}
        facilityId={data?.facility?.id ?? ""}
        facilityState={data?.facility?.state ?? ""}
        facilityContacts={facilityContacts}
        getRowIssues={(row) =>
          issues.filter(
            (issue) =>
              ((issue.modelId === row.person.id && issue.modelName === "Person") ||
                (issue.modelName === "FacilityContact" && issue.modelId === row.id)) &&
              isRcraContactIssue(issue)
          )
        }
        facilityContactIssues={issues.filter(
          (issue) => issue.key === "contacts" && isRcraContactIssue(issue)
        )}
        isReadOnly={isReadOnly}
        refetchQueries={[GET_WASTE_FACILITY_CONTACTS]}
      />
    </Stack>
  );
};
